import { useState, useEffect } from "react";
import { EventCard } from "@/lib/producerTypes";
import { EventGrid } from "./EventGrid";
import { LoadMoreButton } from "./LoadMoreButton";
import { EmptyState } from "./EmptyState";
import { InlineError } from "./InlineError";
import { EventTabSkeleton } from "./EventTabSkeleton";
import { supabase } from "@/integrations/supabase/client";

interface EventsSectionProps {
  producerSlug: string;
}

export const EventsSection = ({ producerSlug }: EventsSectionProps) => {
  const [upcomingEvents, setUpcomingEvents] = useState<EventCard[]>([]);
  const [pastEvents, setPastEvents] = useState<EventCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMorePastEvents, setHasMorePastEvents] = useState(false);
  const [pastEventsOffset, setPastEventsOffset] = useState(0);

  const loadUpcomingEvents = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    
    try {
      // Get the producer bio to find the producer_id
      const { data: bioData, error: bioError } = await supabase
        .from('producers_bio')
        .select('producer_id')
        .eq('slug', producerSlug)
        .single();

      if (bioError) throw bioError;

      const now = new Date().toISOString();
      
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select(`
          id,
          seo_url,
          title,
          date,
          location,
          location_address,
          image_url,
          timezone,
          status
        `)
        .eq('producer_id', bioData.producer_id)
        .eq('status', 'published')
        .gte('date', now)
        .order('date', { ascending: true });

      if (eventsError) throw eventsError;

      const transformedEvents: EventCard[] = eventsData.map(event => ({
        id: event.id,
        slug: event.seo_url,
        title: event.title,
        startAt: event.date,
        venueName: event.location,
        city: extractCity(event.location_address),
        region: extractRegion(event.location_address),
        country: extractCountry(event.location_address),
        imageUrl: event.image_url,
        timezone: event.timezone || 'UTC',
        status: event.status as "scheduled" | "canceled" | "postponed"
      }));

      setUpcomingEvents(transformedEvents);
    } catch (e: any) {
      setError(e.message ?? "Error loading upcoming events");
    } finally {
      setLoading(false);
    }
  };

  const loadPastEvents = async (reset = false) => {
    if (pastEventsLoading) return;
    setPastEventsLoading(true);
    
    try {
      // Get the producer bio to find the producer_id
      const { data: bioData, error: bioError } = await supabase
        .from('producers_bio')
        .select('producer_id')
        .eq('slug', producerSlug)
        .single();

      if (bioError) throw bioError;

      const currentOffset = reset ? 0 : pastEventsOffset;
      const now = new Date().toISOString();
      
      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select(`
          id,
          seo_url,
          title,
          date,
          location,
          location_address,
          image_url,
          timezone,
          status
        `)
        .eq('producer_id', bioData.producer_id)
        .eq('status', 'published')
        .lt('date', now)
        .order('date', { ascending: false })
        .range(currentOffset, currentOffset + 9); // Load 10 at a time (0-9)

      if (eventsError) throw eventsError;

      const transformedEvents: EventCard[] = eventsData.map(event => ({
        id: event.id,
        slug: event.seo_url,
        title: event.title,
        startAt: event.date,
        venueName: event.location,
        city: extractCity(event.location_address),
        region: extractRegion(event.location_address),
        country: extractCountry(event.location_address),
        imageUrl: event.image_url,
        timezone: event.timezone || 'UTC',
        status: event.status as "scheduled" | "canceled" | "postponed"
      }));

      setPastEvents(prev => reset ? transformedEvents : [...prev, ...transformedEvents]);
      setHasMorePastEvents(transformedEvents.length === 10);
      setPastEventsOffset(reset ? 10 : currentOffset + 10);
    } catch (e: any) {
      setError(e.message ?? "Error loading past events");
    } finally {
      setPastEventsLoading(false);
    }
  };

  const extractCity = (address?: string): string | undefined => {
    if (!address) return undefined;
    const parts = address.split(',');
    return parts[parts.length - 3]?.trim();
  };

  const extractRegion = (address?: string): string | undefined => {
    if (!address) return undefined;
    const parts = address.split(',');
    const regionPart = parts[parts.length - 2]?.trim();
    return regionPart?.split(' ')[0];
  };

  const extractCountry = (address?: string): string | undefined => {
    if (!address) return undefined;
    const parts = address.split(',');
    return parts[parts.length - 1]?.trim();
  };

  useEffect(() => {
    loadUpcomingEvents();
    loadPastEvents(true);
  }, [producerSlug]);

  return (
    <section className="mt-16 space-y-12">
      {/* Error state */}
      {error && <InlineError message={error} onRetry={() => { loadUpcomingEvents(); loadPastEvents(true); }} />}

      {/* Loading skeleton for initial load */}
      {loading && upcomingEvents.length === 0 && pastEvents.length === 0 ? (
        <EventTabSkeleton />
      ) : (
        <>
          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-yellow-400 bg-clip-text text-transparent">
                ✨ Upcoming Events ✨
              </h3>
              <EventGrid items={upcomingEvents} />
            </div>
          )}

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div className="space-y-6">
              <h3 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
                🌈 Past Events 💫
              </h3>
              <EventGrid items={pastEvents} />
              
              {/* Load more past events button */}
              {hasMorePastEvents && !pastEventsLoading && (
                <LoadMoreButton onLoadMore={() => loadPastEvents(false)} loading={pastEventsLoading} />
              )}
            </div>
          )}

          {/* Empty state - only show if no events at all */}
          {!loading && !pastEventsLoading && upcomingEvents.length === 0 && pastEvents.length === 0 && (
            <EmptyState tab="upcoming" />
          )}
        </>
      )}
    </section>
  );
};